import { resolveInject } from "app/di";
import { ISparqlExecutor } from "app/services/infrastructure/sparql-executor";
import { SparqlRecord, SparqlResultParser } from "app/services/infrastructure/sparql-result-parser";
import { useTheme } from "app/theme-context";
import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape, { ElementDefinition } from "cytoscape";
import fcose from "cytoscape-fcose";

// Register fcose layout
cytoscape.use(fcose);

interface IComponentProps {
    term_iri: string;
    query: string;
}

// Cytoscape Stylesheet
const stylesheet: cytoscape.StylesheetStyle[] = [
    {
        selector: "node",
        style: {
            content: "data(label)",
            "text-valign": "center",
            color: "black",
            "text-outline-width": 1,
            "text-outline-color": "#fff",
            "background-color": "data(color)",
            "border-width": 1,
            "border-color": "black",
            "font-size": "12px",
            padding: "10px"
        }
    },
    {
        selector: "edge",
        style: {
            "curve-style": "bezier",
            "target-arrow-shape": "triangle",
            label: "data(label)",
            "font-size": "10px",
            color: "gray",
            "line-color": "black",
            "target-arrow-color": "black"
        }
    },
    {
        selector: ".focused",
        style: {
            "border-width": 3,
            "z-index": 100
        }
    }
];

const RelationsGraph: React.FC<IComponentProps> = ({ term_iri, query }) => {
    const { cssProperties } = useTheme();
    const sparqlExecutor = resolveInject(ISparqlExecutor);
    const [elements, setElements] = React.useState<ElementDefinition[]>([]);
    const cyRef = React.useRef<cytoscape.Core | null>(null);

    // Convert SPARQL results into Cytoscape elements
    const sparqlRecordToCytoscapeElements = (result: SparqlRecord[]): ElementDefinition[] => {
        const elements: ElementDefinition[] = [];
        const nodeIds = new Set<string>();

        // Central node
        elements.push({
            data: { id: term_iri, label: term_iri, color: cssProperties.backgroundColor },
            classes: "focused"
        });
        nodeIds.add(term_iri);

        // Add related nodes and edges
        for (let r of result) {
            const source = r["entry_iri"]?.value as string;
            const targetId = r["object_iri"]?.value as string;
            const targetName = (r["object_text"]?.value as string) || targetId;
            const label =
                (r["predicate_text"]?.value as string) || (r["predicate_iri"]?.value as string);

            if (!nodeIds.has(targetId)) {
                elements.push({
                    data: {
                        id: targetId,
                        label: targetName || targetId,
                        color: "#FFFFFF"
                    }
                });
                nodeIds.add(targetId);
            }

            elements.push({
                data: {
                    id: `${source}-${targetId}-${label}`,
                    source,
                    target: targetId,
                    label
                },
                group: "edges"
            });
        }

        return elements;
    };

    // Fetch and update graph data
    React.useEffect(() => {
        const fetchResults = async () => {
            const result = await sparqlExecutor.execute(query, [
                { name: "term_iri", type: "uri", value: term_iri }
            ]);
            const records = SparqlResultParser.parse(result);
            setElements(sparqlRecordToCytoscapeElements(records));
        };
        fetchResults();
    }, [term_iri, query]);

    // Layout config (using fcose)
    const layout = {
        name: "fcose",
        quality: "default",
        randomize: true,
        animate: true,
        fit: true,
        nodeSeparation: 50,
        nodeRepulsion: 5000,
        idealEdgeLength: 120,
        edgeElasticity: 100,
        gravity: 0.25,
        padding: 50
    };

    // Auto-resize on window changes
    React.useEffect(() => {
        const handleResize = () => {
            if (cyRef.current) {
                cyRef.current.resize();
                cyRef.current.fit();
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    React.useEffect(() => {
        if (cyRef.current && elements.length > 0) {
            const cy = cyRef.current;
            cy.elements().remove();
            cy.add(elements);
            const layoutInstance = cy.layout({ ...layout } as any);
            layoutInstance.run();
            cy.fit();
        }
    }, [elements]);

    const containerStyle = { width: "100%", minHeight: "600px" };

    return (
        <Card>
            <CardHeader title="Web of Relations" sx={{ p: 1, pb: 0 }} />
            <CardContent sx={{ p: 4 }}>
                <div style={containerStyle}>
                    {elements.length > 0 && (
                        <CytoscapeComponent
                            key={term_iri}
                            cy={(cy) => {
                                cyRef.current = cy;
                            }}
                            elements={elements}
                            style={{
                                ...containerStyle,
                                backgroundColor: "#F7F7F7",
                                borderRadius: "8px",
                                padding: "12px"
                            }}
                            layout={layout}
                            stylesheet={stylesheet}
                            zoomingEnabled={true}
                            boxSelectionEnabled={false}
                            autounselectify={false}
                        />
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default RelationsGraph;
